import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useSocket, useSocketEvent } from '../../hooks/useSocket';
import { useUrlTenant } from '../../hooks/useUrlTenant';
import { SocketEvents } from '../../constants/socket-events';
import { OPERATOR_STATUSES, parseStatus, type OperatorStatus } from '../../lib/operatorStatus';
import { operatorStatusService } from '../../services/operatorStatus.service';
import { inboxService } from '../../services/inbox.service';
import { createLogger } from '../../utils/logger';
import { Modal } from '../common/Modal';

const log = createLogger('StatusSwitch');

const DOT: Record<OperatorStatus, string> = {
  working: 'bg-ok',
  break: 'bg-warn',
  off: 'bg-muted',
};

/** The most open conversations one release pass covers (the inbox list cap). */
const RELEASE_LIMIT = 200;

type OffShift = { ids: string[]; busy: boolean; failed: number };

/**
 * Your status in the workspace you are in: working / on a break / off shift. An admin can give conversations
 * only to people who are working and connected (the gateway enforces it); your status never stops you taking
 * one yourself. Shown to anyone who can reply in this workspace.
 *
 * Going off shift while holding open conversations asks first: release them to the queue for the next
 * operator, or keep them (finishing a case after the shift is allowed). A break never asks — it is short.
 *
 * Another tab switching, or the gateway resetting "working" left on from more than 12 hours ago, arrives as
 * `operator:statusChanged`; the status is also re-read on every (re)connect.
 */
export function StatusSwitch() {
  const { t } = useTranslation();
  const { apiFetch, canIn } = useAuth();
  const { isConnected } = useSocket();
  const tenant = useUrlTenant();
  const tenantId = tenant && canIn('reply', tenant.id) ? tenant.id : null;
  const [status, setStatus] = useState<OperatorStatus | null>(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);
  const [offShift, setOffShift] = useState<OffShift | null>(null);
  const ref = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const listId = useId();

  // A dialog opened in another workspace must not release its conversations under this one's status.
  useEffect(() => setOffShift(null), [tenantId]);

  useEffect(() => {
    setStatus(null);
    if (!tenantId) return;
    let live = true;
    operatorStatusService
      .get(apiFetch, tenantId)
      .then((s) => {
        if (live) setStatus(s);
      })
      .catch((err) => log.warn('operator status load failed', err));
    return () => {
      live = false;
    };
  }, [apiFetch, tenantId, isConnected]);

  const onChanged = useCallback(
    (data: { tenantId?: string; status?: unknown }) => {
      if (tenantId && data?.tenantId === tenantId) setStatus(parseStatus(data.status));
    },
    [tenantId],
  );
  useSocketEvent(SocketEvents.OPERATOR_STATUS_CHANGED, onChanged);

  const close = useCallback((refocus: boolean) => {
    setOpen(false);
    if (refocus) triggerRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) close(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close(true);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close]);

  const save = useCallback(
    async (next: OperatorStatus) => {
      if (!tenantId) return false;
      setSaving(true);
      setError(false);
      try {
        setStatus(await operatorStatusService.set(apiFetch, tenantId, next));
        return true;
      } catch (err) {
        log.warn('operator status save failed', err);
        setError(true);
        return false;
      } finally {
        setSaving(false);
      }
    },
    [apiFetch, tenantId],
  );

  const choose = async (next: OperatorStatus) => {
    close(true);
    if (!tenantId || next === status) return;
    if (next === 'off') {
      // Ask only when there is something to hand over. If the list cannot be read, go off shift anyway:
      // the conversations stay theirs, exactly as before statuses existed.
      try {
        const { list } = await inboxService.list(apiFetch, { view: 'mine' }, { tenantId, limit: RELEASE_LIMIT });
        if (list.length > 0) {
          setOffShift({ ids: list.map((c) => c.id), busy: false, failed: 0 });
          return;
        }
      } catch (err) {
        log.warn('open conversations lookup failed', err);
      }
    }
    await save(next);
  };

  const finishShift = async (release: boolean) => {
    if (!offShift) return;
    setOffShift({ ...offShift, busy: true, failed: 0 });
    if (!(await save('off'))) {
      setOffShift({ ...offShift, busy: false });
      return;
    }
    if (!release) {
      setOffShift(null);
      return;
    }
    let failed = 0;
    // One at a time: each release is journalled and announced, and a handful is the usual case.
    for (const id of offShift.ids) {
      try {
        await inboxService.release(apiFetch, id);
      } catch (err) {
        failed++;
        log.warn('release on shift end failed', err);
      }
    }
    setOffShift(failed === 0 ? null : { ids: [], busy: false, failed });
  };

  if (!tenantId || !status) return null;

  const current = t(`operatorStatus.${status}`);
  return (
    <div className="relative" ref={ref}>
      <button
        ref={triggerRef}
        onClick={() => (open ? close(false) : setOpen(true))}
        disabled={saving}
        className="min-h-11 px-2 inline-flex items-center gap-1.5 rounded-control text-sm text-ink hover:bg-surface-2 transition-colors disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        aria-expanded={open}
        aria-controls={listId}
        aria-label={t('operatorStatus.label', { status: current })}
        title={error ? t('operatorStatus.saveFailed') : t('operatorStatus.label', { status: current })}
      >
        <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${DOT[status]}`} aria-hidden="true" />
        <span className="hidden sm:inline font-medium">{current}</span>
        {error && <span className="text-xs font-semibold text-danger" aria-hidden="true">!</span>}
        <svg className="w-3 h-3 text-muted" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M7 10l5 5 5-5z" />
        </svg>
      </button>
      {open && (
        <div
          id={listId}
          aria-label={t('operatorStatus.title')}
          className="absolute top-full right-0 mt-2 w-56 bg-surface border border-line rounded-card shadow-pop z-[100] py-1"
        >
          <p className="px-3 pt-2 pb-1 text-xs text-muted">{t('operatorStatus.hint')}</p>
          {OPERATOR_STATUSES.map((s) => (
            <button
              key={s}
              onClick={() => void choose(s)}
              aria-current={s === status ? 'true' : undefined}
              className="w-full min-h-11 px-3 flex items-center justify-start gap-2 text-left text-sm text-ink hover:bg-surface-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${DOT[s]}`} aria-hidden="true" />
              <span className={s === status ? 'font-semibold' : ''}>{t(`operatorStatus.${s}`)}</span>
              {s === status && (
                <svg className="ml-auto w-4 h-4 text-primary" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M9 16.2l-3.5-3.5L4 14.2l5 5 11-11-1.5-1.5z" />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
      {offShift && (
        <Modal
          title={offShift.failed ? t('operatorStatus.releasePartialTitle') : t('operatorStatus.offShiftTitle')}
          onClose={() => {
            if (!offShift.busy) setOffShift(null);
          }}
          locked={offShift.busy}
        >
          <div className="px-5 py-4 text-sm text-ink">
            {offShift.failed
              ? t('operatorStatus.releasePartial', { count: offShift.failed })
              : t('operatorStatus.offShiftBody', { count: offShift.ids.length })}
          </div>
          <div className="px-5 py-3 border-t border-line flex flex-wrap items-center justify-end gap-2 bg-surface-2 rounded-b-lg">
            {offShift.failed ? (
              <button
                type="button"
                onClick={() => setOffShift(null)}
                className="min-h-11 px-4 text-sm font-medium text-on-primary bg-primary rounded-control hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                {t('common.close')}
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => void finishShift(false)}
                  disabled={offShift.busy}
                  className="min-h-11 px-4 text-sm text-ink rounded-control hover:bg-surface disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                >
                  {t('operatorStatus.keepThem')}
                </button>
                <button
                  type="button"
                  onClick={() => void finishShift(true)}
                  disabled={offShift.busy}
                  className="min-h-11 px-4 text-sm font-medium text-on-primary bg-primary rounded-control hover:opacity-90 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                >
                  {offShift.busy ? t('common.working') : t('operatorStatus.releaseThem')}
                </button>
              </>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}
